import React, { useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Redirect, useHistory } from 'react-router-dom';
import queryString from 'query-string';
import { ConsentError, ReasonCode, RoutePath, TokenParams } from '../../constants';
import {
  checkTokenExpiration,
  getErrorProps,
  getParamsByQueryParamEnum,
  getQueryParamString,
  getToken,
  getVinsToConsent,
  hasAppQueryParams,
  logToRemote,
  redirect,
  spinnerOff,
  spinnerOn,
} from '../../util';
import { REACT_APP_SUBSCRIBER_URI, REACT_APP_SUBSCRIBER_CTX, REACT_APP_SUBSCRIBER_GET_VEHS } from '../../config';

import PropTypes from 'prop-types';
import OnStarContainer from '../OnStarContainer';
import VehicleSelection from './VehicleSelection';

const addIsSelectedField = (eligibleVehResp) => {
  const eligibleVins = eligibleVehResp?.eligibleVehicles?.eligibleVins;
  if (eligibleVins) {
    eligibleVins.forEach((eligibleVin) => {
      eligibleVin.isSelected = eligibleVin.isConsented;
    });
  }
};

const getEligibleVehResp = async (clientId, history, params, setEligibleVehResp, spinnerRef) => {
  try {
    spinnerOn(spinnerRef);

    const accessToken = await getToken(clientId);
    const isTokenExpired = checkTokenExpiration(accessToken);

    if (isTokenExpired) {
      logToRemote({
        code: 401,
        level: 'error',
        desc: 'Access token expired. Logging out',
        src: 'EligibleVehicle/index.js::getEligibleVehResp::isTokenExpired',
      });
      history.push({ pathname: RoutePath.logout, search: getQueryParamString() });
    } else {
      const eligibleVehUrl = `${REACT_APP_SUBSCRIBER_URI}/${REACT_APP_SUBSCRIBER_CTX}/${clientId}/${REACT_APP_SUBSCRIBER_GET_VEHS}`;
      const eligibleVehUrlWithQueryParams = `${eligibleVehUrl}?${new URLSearchParams(params)}`;
      const eligibleVehHeaders = { Authorization: `Bearer ${accessToken}` };

      const resp = await axios.get(eligibleVehUrlWithQueryParams, {
        headers: eligibleVehHeaders,
      });
      if (resp) {
        const { data: eligibleVehData } = resp;
        addIsSelectedField(eligibleVehData);
        setEligibleVehResp(eligibleVehData);
      }
    }
  } catch (e) {
    const code = e.response?.status || e.code || '500';
    logToRemote({
      code,
      level: 'error',
      desc: 'Exception at EligibleVehicle call',
      src: 'EligibleVehicle/index.js::getEligibleVehResp',
    });

    const hasParams = hasAppQueryParams();
    if (!hasParams) {
      const errorParams = {
        code: 400,
        level: 'error',
        src: 'EligibleVehicle/index.js::getEligibleVehResp::!hasParams',
        desc: 'Missing App Query Parameters',
        status: 'error',
        moreInfo: `consent.error.${ConsentError.missingQueryParams}`,
      };
      history.push({ pathname: RoutePath.error, search: getQueryParamString(), state: errorParams });
    } else {
      const errorProps = getErrorProps(e);
      history.push({ pathname: RoutePath.error, search: getQueryParamString(), state: errorProps });
    }
  } finally {
    spinnerOff(spinnerRef);
  }
};

const AuthErrorParams = {
  pathname: '/auth-error',
  state: {
    showError: 'tampered',
  },
  search: getQueryParamString(),
};

const EligibleVehicle = ({ tokenResp, eligibleVehResp, setEligibleVehResp, spinnerRef }) => {
  const history = useHistory();
  const { i18n } = useTranslation();
  const { clientId, redirectUri, responseType, scope, state } = getParamsByQueryParamEnum();
  const clientName = eligibleVehResp?.ConsentScopeService?.name;
  const reasonCode = eligibleVehResp?.reasonCode;
  const selectedVehs = getVinsToConsent(eligibleVehResp);

  useEffect(() => {
    const profileId = tokenResp?.account?.idToken?.ProfileID;
    const email = tokenResp?.account?.idToken?.email;
    sessionStorage.setItem(TokenParams.profileId, profileId || '');
    const queryStr = window.location.search;
    const params = queryString.parse(queryStr);
    logToRemote({
      code: 200,
      level: 'info',
      src: 'EligibleVehicle/index.js::useEffect::extractProfileId',
      queryStrLen: queryStr.length,
      paramLen: Object.keys(params).length,
      email,
    });
  }, [tokenResp?.account?.idToken?.email, tokenResp?.account?.idToken?.ProfileID]);

  // Call backend to get eligibleVehResp
  useEffect(() => {
    const params = {
      scope,
      profileId: tokenResp?.account?.idToken?.ProfileID,
      redirectUri,
      responseType,
    };

    if (!eligibleVehResp) {
      getEligibleVehResp(clientId, history, params, setEligibleVehResp, spinnerRef);
    }
  }, [clientId, scope, redirectUri, responseType, history, tokenResp, eligibleVehResp, setEligibleVehResp, spinnerRef]);

  const onCancel = () => {
    redirect(redirectUri, {
      error: ConsentError.accessDenied,
      state,
    });
  };

  const onCheckBox = (eligibleVin) => {
    eligibleVin.isSelected = !eligibleVin.isSelected;
    eligibleVehResp.eligibleVehicles.eligibleVins = [...eligibleVehResp.eligibleVehicles.eligibleVins];
    setEligibleVehResp({ ...eligibleVehResp });
  };

  const onContinue = () => {
    history.push({ pathname: RoutePath.consentConfirmation, search: getQueryParamString() });
  };

  if (sessionStorage.getItem('error') === 'parsing error') {
    return <Redirect to={AuthErrorParams} />;
  }

  if (reasonCode === ReasonCode.foundOnStarHome) {
    return <Redirect to={{ pathname: RoutePath.consentConfirmation, search: getQueryParamString() }} />;
  }

  return (
    <OnStarContainer title={i18n.t('papi.general.selectVehicles.lable')}>
      <section>
        <p className='subheading'>{clientName}</p>
        <p>{i18n.t('papi.general.selectVehicle.message.a', { 0: clientName })}</p>
        <div>
          <div style={{ marginTop: '2em', marginBottom: '2em' }}>
            <VehicleSelection eligibleVins={eligibleVehResp?.eligibleVehicles?.eligibleVins} onCheckBox={onCheckBox} />
          </div>
          <button
            id='continue-btn'
            style={{ marginBottom: '1em' }}
            className='square-button'
            onClick={onContinue}
            disabled={!eligibleVehResp || selectedVehs.length === 0}
          >
            {i18n.t('papi.general.continue.lable')}
          </button>
          <button id='cancel-btn' className='clear-button' onClick={onCancel}>
            {i18n.t('papi.general.cancel.lable')}
          </button>
        </div>
      </section>
    </OnStarContainer>
  );
};

EligibleVehicle.propTypes = {
  tokenResp: PropTypes.object,
  account: PropTypes.object,
  eligibleVehResp: PropTypes.object,
  setEligibleVehResp: PropTypes.func,
  spinnerRef: PropTypes.object,
};

export default EligibleVehicle;
